import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { FormikProvider, useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { Button } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import { validator } from 'packages/kakapo-ui-services/factories/ValidatorFactory';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import CertificatePreviewContainer from 'packages/kakapo-ui-application/pages/learning/components/certificates/CertificatePreviewContainer';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';

const Sticky = styled.div`
	position: sticky;
	top: 90px;
`;

export default function LearningCertificateEdit({
	template,
}: {
	template?: graphql.LearningCertificateTemplate;
}) {
	const { t } = useTranslation();

	const { addToast } = useToasts();
	const viewer = useViewer();
	const history = useHistory();

	const theme = useTheme() as any;

	const [createTemplate, { loading: createLoading }] =
		graphql.useCreateLearningCertificateTemplateMutation();
	const [updateTemplate, { loading: updateLoading }] =
		graphql.useUpdateLearningCertificateTemplateMutation();

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			title: template?.title ?? '',
			design: graphql.LearningCertificateDesign.ColorOutline,
			logo: template?.logo?.id ?? undefined,
			signature: template?.signature?.id ?? undefined,
			signerName: template?.signerName ?? '',
			signerTitle: template?.signerTitle ?? '',
			accentColor: template?.accentColor ?? theme.colors.primary,
		},
		validationSchema: validator.object({
			title: validator.string().min(1).required(),
		}),
		onSubmit: async (values) => {
			let newTemplate: graphql.LearningCertificateTemplate | undefined;

			if (template) {
				await updateTemplate({
					variables: {
						input: {
							id: template.id,
							title: values.title,
							design: values.design,
							signerName: values.signerName?.length > 0 ? values.signerName : undefined,
							signerTitle: values.signerTitle?.length > 0 ? values.signerTitle : undefined,
							accentColor: values.accentColor?.length > 0 ? values.accentColor : undefined,
						},
					},
				});

				addToast(t('common.settings.learningCertificateTemplates.edit.success'), {
					appearance: 'success',
					autoDismiss: true,
				});
			} else {
				const result = await createTemplate({
					variables: {
						input: {
							title: values.title,
							design: values.design,
							signerName: values.signerName?.length > 0 ? values.signerName : undefined,
							signerTitle: values.signerTitle?.length > 0 ? values.signerTitle : undefined,
							accentColor: values.accentColor?.length > 0 ? values.accentColor : undefined,
						},
					},
				});

				addToast(t('common.settings.learningCertificateTemplates.create.success'), {
					appearance: 'success',
					autoDismiss: true,
				});

				newTemplate = result.data?.createLearningCertificateTemplate
					.template as graphql.LearningCertificateTemplate;
			}

			if (newTemplate) {
				history.push(`/settings/learning/certificates/templates/${newTemplate.id}`);
			}
		},
	});

	return (
		<FormikProvider value={formik}>
			<Flex>
				<Box width={[6 / 12]} pr={3}>
					<Flex flexDirection="column" my={3}>
						<Box mb={2}>
							<Box mb={3}>
								<SubSectionTitle>
									{t(
										`common.settings.learningCertificateTemplates.${
											template ? 'edit' : 'create'
										}.details`,
									)}
								</SubSectionTitle>
							</Box>
							<HorizontalRule />
						</Box>
						<DetailsTableForm
							formValues={[
								{
									label: t('common.settings.learningCertificateTemplates.edit.title'),
									name: 'title',
									type: DetailsTableFormValueTypes.Text,
								},
								{
									label: t('common.settings.learningCertificateTemplates.edit.signerName'),
									name: 'signerName',
									type: DetailsTableFormValueTypes.Text,
								},
								{
									label: t('common.settings.learningCertificateTemplates.edit.signerTitle'),
									name: 'signerTitle',
									type: DetailsTableFormValueTypes.Text,
								},
								{
									label: t('common.settings.learningCertificateTemplates.edit.accentColor'),
									name: 'accentColor',
									type: DetailsTableFormValueTypes.ColorPicker,
								},
							]}
						/>
					</Flex>
					<HorizontalRule />
					<Box mt={3}>
						<Flex justifyContent="space-between">
							<Button
								type="secondary"
								link="/settings/learning"
								title={t('common.settings.learningCertificateTemplates.edit.back')}
							/>
							<Button
								onClick={() => formik.submitForm()}
								disabled={loading && !formik.isValid}
								title={t('common.save')}
								loading={loading}
							/>
						</Flex>
					</Box>
				</Box>
				<Box width={[6 / 12]} my={3} pl={3}>
					<Box mb={3}>
						<SubSectionTitle>
							{t('common.settings.learningCertificateTemplates.edit.preview')}
						</SubSectionTitle>
					</Box>
					<HorizontalRule />
					<Sticky>
						<Box my={3}>
							<CertificatePreviewContainer
								accentColor={formik.values.accentColor}
								attendeeName={`${viewer!.firstName} ${viewer!.lastName}`}
								eventDate={new Date()}
								eventTitle={t(
									'common.settings.learningCertificateTemplates.edit.placeholders.title',
								)}
								eventDescription={t(
									'common.settings.learningCertificateTemplates.edit.placeholders.description',
								)}
								signerName={formik.values.signerName}
								signerTitle={formik.values.signerTitle}
								signature={template?.signature?.source.url}
								logo={template?.logo?.source.url}
							/>
						</Box>
					</Sticky>
				</Box>
			</Flex>
		</FormikProvider>
	);
}

export function LearningCertificateEditWithID({ id }: { id: string }) {
	const query = graphql.useLearningCertificateTemplateQuery({ variables: { id } });

	if (query.data) {
		return (
			<LearningCertificateEdit
				template={query.data.learningCertificateTemplate as graphql.LearningCertificateTemplate}
			/>
		);
	}

	return <Spinner isLoading size="18px" />;
}
